import { DateFilter } from '@chiroup/core/types/AdminMetrics.type';
import * as Tabs from '@radix-ui/react-tabs';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  AlertCircle,
  BarChart3,
  CheckCircle,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  Clock,
  Filter,
  Loader2,
  Target,
  TrendingUp,
  Trophy,
  UserPlus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useQuery } from 'react-query';
import Header from '../layout/Header';
import AnalyticsLoading from './components/AnalyticsLoading';
import DashboardNavigation from './components/DashboardNavigation';
import DonutChart from './components/DonutChart';
import FunnelChart from './components/FunnelChart';
import MetricCard from './components/MetricCard';
import MetricInfoTooltip from './components/MetricInfoTooltip';
import ModernDatePicker from './components/ModernDatePicker';
import { TimeToGoLiveCard } from './components/TimeToGoLiveCard';
import { TrendLineChart } from './components/TrendLineChart';
import analyticsService from './services/analytics.service';

// Helper component for rendering donut chart segments with hover effects
const DonutSegments: React.FC<{
  data: Array<{ name: string; value: number; color: string }>;
  total: number;
  onSegmentHover?: (index: number | null) => void;
  hoveredIndex: number | null;
}> = ({ data, total, onSegmentHover, hoveredIndex }) => {
  const baseRadius = 110;
  const strokeWidth = 48;

  let currentAngle = -90; // Start from top

  return (
    <g transform="translate(170, 170)">
      {data.map((item, index) => {
        const percentage = item.value / total;
        const angle = percentage * 360;
        const largeArcFlag = angle > 180 ? 1 : 0;

        // Increase radius slightly on hover for "bump" effect
        const radius = hoveredIndex === index ? baseRadius + 5 : baseRadius;

        const startX = radius * Math.cos((currentAngle * Math.PI) / 180);
        const startY = radius * Math.sin((currentAngle * Math.PI) / 180);

        const angleBeforeIncrement = currentAngle;
        currentAngle += angle;

        const endX = radius * Math.cos((currentAngle * Math.PI) / 180);
        const endY = radius * Math.sin((currentAngle * Math.PI) / 180);

        const pathData = `
          M ${startX} ${startY}
          A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}
        `;

        return (
          <motion.path
            key={index}
            d={pathData}
            fill="none"
            stroke={item.color}
            strokeWidth={strokeWidth}
            strokeLinecap="butt"
            className="cursor-pointer transition-all duration-200"
            onMouseEnter={() => onSegmentHover?.(index)}
            onMouseLeave={() => onSegmentHover?.(null)}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ delay: 0.3 + index * 0.1, duration: 0.8 }}
            whileHover={{
              filter: 'brightness(1.1) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))',
            }}
          />
        );
      })}
    </g>
  );
};

const OnboardingDashboard = () => {
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    type: 'yearToDate',
  });
  const [minimumDurationComplete, setMinimumDurationComplete] = useState(false);
  const [activeTab, setActiveTab] = useState('metrics');
  const [hoveredCoach1Index, setHoveredCoach1Index] = useState<number | null>(null);
  const [hoveredCoach2Index, setHoveredCoach2Index] = useState<number | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['analytics-metrics', 'onboarding', dateFilter],
    () => analyticsService.getMetrics('onboarding', dateFilter),
    {
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 minutes
      keepPreviousData: true,
    }
  );

  // Prepare metrics data before any conditional returns to follow Rules of Hooks
  const allMetrics = data?.metrics || [];

  // Show full loading screen for initial load OR filter changes
  const shouldShowLoading = isLoading || isFetching || !minimumDurationComplete;

  if (shouldShowLoading) {
    return <AnalyticsLoading onMinimumDurationComplete={() => setMinimumDurationComplete(true)} />;
  }

  const dateOptions = [
    { value: 'today', label: 'Today' },
    { value: 'past7days', label: 'Past 7 Days' },
    { value: 'monthToDate', label: 'Month to Date' },
    { value: 'past90days', label: 'Past 90 Days' },
    { value: 'yearToDate', label: 'Year to Date' },
    { value: 'custom', label: 'Custom Range' },
  ] as const;

  // Handle date filter change
  const handleFilterChange = (type: DateFilter['type']) => {
    if (type === 'custom') {
      setDateFilter({
        type,
        startDate: dateFilter.startDate || new Date().toISOString().split('T')[0],
        endDate: dateFilter.endDate || new Date().toISOString().split('T')[0],
      });
    } else {
      setDateFilter({ type });
    }
  };

  // Handle custom date change
  const handleDateChange = (field: 'startDate' | 'endDate', date: Date | null) => {
    if (date) {
      setDateFilter({
        ...dateFilter,
        [field]: date.toISOString().split('T')[0],
      });
    }
  };

  // Convert date strings to Date objects for DatePicker
  const getDateValue = (dateString?: string): Date | undefined => {
    return dateString ? new Date(dateString + 'T00:00:00') : undefined;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
      <Header />
      <DashboardNavigation currentDashboardId="onboarding" />

      <div className="p-6">
        {/* Enhanced Filter Section - Support Dashboard Style */}
        <div className="mb-8">
          <motion.div
            className="bg-gradient-to-r from-green-600 via-emerald-700 to-teal-700 rounded-2xl p-6 shadow-xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              {/* Title Section */}
              <motion.div
                className="flex flex-col gap-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                {/* Title and Icon Row */}
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                    <UserPlus className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Onboarding Performance Metrics</h2>
                    <p className="text-green-100 text-sm">
                      Track EHR clinic onboarding progress and success rates
                    </p>
                  </div>
                </div>

                {/* Live Data Indicator */}
                <motion.div
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 self-start ml-14"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  {/* Animated Pulse Dot */}
                  <div className="relative flex items-center">
                    <motion.div
                      className="w-2 h-2 bg-green-400 rounded-full"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.8, 1, 0.8],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    {/* Ripple Effect */}
                    <motion.div
                      className="absolute inset-0 bg-green-400 rounded-full"
                      animate={{
                        scale: [1, 2.5],
                        opacity: [0.6, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                  </div>
                  <span className="text-white/90 text-xs font-medium tracking-wide">LIVE DATA</span>
                  <span className="text-white/70 text-xs">
                    • Updated{' '}
                    {new Date().toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </span>
                </motion.div>
              </motion.div>

              {/* Date Filter Controls */}
              <motion.div
                className="flex items-center gap-1.5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                {dateOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleFilterChange(option.value)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 border-2 whitespace-nowrap ${
                      dateFilter.type === option.value
                        ? 'bg-white text-green-700 border-white shadow-lg'
                        : 'text-white/90 border-white/30 hover:border-white/50 hover:bg-white/10'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </motion.div>
            </div>

            {/* Custom Date Pickers */}
            {dateFilter.type === 'custom' && (
              <motion.div
                className="mt-4 pt-4 border-t border-white/20"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/90 font-medium">Start Date</span>
                    <ModernDatePicker
                      selected={getDateValue(dateFilter.startDate)}
                      onChange={date => handleDateChange('startDate', date)}
                      placeholder="Start date"
                    />
                  </div>
                  <span className="text-white/60 text-sm hidden sm:block">to</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/90 font-medium">End Date</span>
                    <ModernDatePicker
                      selected={getDateValue(dateFilter.endDate)}
                      onChange={date => handleDateChange('endDate', date)}
                      placeholder="End date"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Tab Navigation - Same style as Support Dashboard */}
        <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
          <Tabs.List className="grid w-full grid-cols-2 sm:grid-cols-4 gap-3 p-2 bg-gradient-to-r from-slate-50/50 to-gray-50/80 backdrop-blur-sm rounded-3xl border border-white/20 shadow-2xl max-w-4xl mx-auto mb-8">
            <Tabs.Trigger
              value="metrics"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-green-500 data-[state=active]:via-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Target className="h-4 w-4" />
                <span>Metrics</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="funnel"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-500 data-[state=active]:via-indigo-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Filter className="h-4 w-4" />
                <span>Funnel</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="visualizations"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:via-green-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <BarChart3 className="h-4 w-4" />
                <span>Visualizations</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="leaderboard"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-500 data-[state=active]:via-orange-500 data-[state=active]:to-yellow-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Trophy className="h-4 w-4" />
                <span>Leaderboard</span>
              </motion.div>
            </Tabs.Trigger>
          </Tabs.List>

          {/* Tab Content */}
          <Tabs.Content value="metrics" className="focus:outline-none">
            {/* EHR New Clinics Metric */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-purple-50/30 to-indigo-50/30 dark:from-gray-800/50 dark:via-purple-900/20 dark:to-indigo-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-purple-600" />
                  EHR Clinics in Onboarding
                </h3>
              </motion.div>

              {/* EHR Clinics Metric Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* EHR New Clinics (Date Filtered) */}
                <div className="flex justify-center">
                  {(() => {
                    const ehrMetric = allMetrics.find(m => m.id === 'ehr-new-clinics-onboarding');

                    if (ehrMetric) {
                      return (
                        <motion.div
                          key="ehr-new-clinics-onboarding"
                          initial={{ opacity: 0, y: 30, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ delay: 0.4, type: 'spring', stiffness: 100 }}
                          className="transform hover:scale-105 transition-all duration-300 w-full"
                        >
                          <MetricCard metric={ehrMetric} />
                        </motion.div>
                      );
                    }

                    return (
                      <div className="flex items-center justify-center h-32 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 w-full">
                        <div className="text-center">
                          <CheckCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 dark:text-gray-400 text-sm">
                            No EHR clinics data available
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Total Clinics in Onboarding (No Date Filter) */}
                <div className="flex justify-center">
                  {(() => {
                    const totalMetric = allMetrics.find(m => m.id === 'total-clinics-onboarding');

                    if (totalMetric) {
                      return (
                        <motion.div
                          key="total-clinics-onboarding"
                          initial={{ opacity: 0, y: 30, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ delay: 0.5, type: 'spring', stiffness: 100 }}
                          className="transform hover:scale-105 transition-all duration-300 w-full"
                        >
                          <MetricCard metric={totalMetric} />
                        </motion.div>
                      );
                    }

                    return (
                      <div className="flex items-center justify-center h-32 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 w-full">
                        <div className="text-center">
                          <CheckCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 dark:text-gray-400 text-sm">
                            No total clinics data available
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </motion.div>

            {/* Onboarding Outcomes Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-emerald-50/30 to-red-50/30 dark:from-gray-800/50 dark:via-emerald-900/20 dark:to-red-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 via-green-600 to-red-600 bg-clip-text text-transparent mb-2 flex items-center gap-3">
                  <Target className="w-8 h-8 text-emerald-600" />
                  Onboarding Outcomes
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  These metrics are calculated based on only EHR Onboarding opportunities{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    (Note: These won't include EHR sales and churn saves opportunities)
                  </span>
                </p>
              </motion.div>

              {/* Outcome Metric Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Successfully Onboarded */}
                <div className="flex justify-center">
                  {(() => {
                    const successMetric = allMetrics.find(
                      m => m.id === 'total-successfully-onboarded-clinics'
                    );

                    if (successMetric) {
                      return (
                        <motion.div
                          key="total-successfully-onboarded-clinics-outcome"
                          initial={{ opacity: 0, y: 30, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ delay: 0.7, type: 'spring', stiffness: 100 }}
                          className="transform hover:scale-105 transition-all duration-300 w-full"
                        >
                          <MetricCard metric={successMetric} />
                        </motion.div>
                      );
                    }

                    return null;
                  })()}
                </div>

                {/* Total Unsuccessful Clinics */}
                <div className="flex justify-center">
                  {(() => {
                    const unsuccessMetric = allMetrics.find(
                      m => m.id === 'total-unsuccessful-clinics'
                    );

                    if (unsuccessMetric) {
                      return (
                        <motion.div
                          key="total-unsuccessful-clinics"
                          initial={{ opacity: 0, y: 30, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ delay: 0.8, type: 'spring', stiffness: 100 }}
                          className="transform hover:scale-105 transition-all duration-300 w-full"
                        >
                          <MetricCard metric={unsuccessMetric} />
                        </motion.div>
                      );
                    }

                    return null;
                  })()}
                </div>

                {/* Logo Churn */}
                <div className="flex justify-center">
                  {(() => {
                    const logoChurnMetric = allMetrics.find(m => m.id === 'logo-churn-clinics');

                    if (logoChurnMetric) {
                      return (
                        <motion.div
                          key="logo-churn-clinics"
                          initial={{ opacity: 0, y: 30, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ delay: 0.9, type: 'spring', stiffness: 100 }}
                          className="transform hover:scale-105 transition-all duration-300 w-full"
                        >
                          <MetricCard metric={logoChurnMetric} />
                        </motion.div>
                      );
                    }

                    return null;
                  })()}
                </div>

                {/* Product Churn */}
                <div className="flex justify-center">
                  {(() => {
                    const productChurnMetric = allMetrics.find(
                      m => m.id === 'product-churn-clinics'
                    );

                    if (productChurnMetric) {
                      return (
                        <motion.div
                          key="product-churn-clinics"
                          initial={{ opacity: 0, y: 30, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ delay: 1.0, type: 'spring', stiffness: 100 }}
                          className="transform hover:scale-105 transition-all duration-300 w-full"
                        >
                          <MetricCard metric={productChurnMetric} />
                        </motion.div>
                      );
                    }

                    return null;
                  })()}
                </div>
              </div>
            </motion.div>

            {/* EHR Checklist Completion Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 dark:from-gray-800/50 dark:via-indigo-900/20 dark:to-purple-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.6, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 bg-clip-text text-transparent mb-2 flex items-center gap-3">
                  <ClipboardCheck className="w-8 h-8 text-indigo-600" />
                  EHR Checklist Completion Status
                  <MetricInfoTooltip
                    info={allMetrics.find(m => m.id === 'ehr-checklist-completion')?.info}
                    query={allMetrics.find(m => m.id === 'ehr-checklist-completion')?.query}
                  />
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Track checklist completion status for closed opportunities{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    (filtered by Go-Live Date)
                  </span>
                </p>
              </motion.div>

              {/* Checklist Status Cards */}
              {(() => {
                const checklistMetric = allMetrics.find(m => m.id === 'ehr-checklist-completion');

                if (
                  !checklistMetric ||
                  !checklistMetric.value ||
                  typeof checklistMetric.value !== 'object'
                ) {
                  return null;
                }

                const data = checklistMetric.value as Record<string, number>;

                // Define the 3 status categories with their display info
                const statusCategories = [
                  {
                    key: 'In progress',
                    label: 'In Progress',
                    color: '#3B82F6',
                    iconName: 'Clock',
                  },
                  {
                    key: '✅ Complete',
                    label: 'Completed',
                    color: '#10B981',
                    iconName: 'CheckCircle2',
                  },
                  {
                    key: 'None',
                    label: 'Not Started',
                    color: '#64748B',
                    iconName: 'Circle',
                  },
                ];

                const iconComponents = {
                  Clock: Clock,
                  CheckCircle2: CheckCircle2,
                  Circle: Circle,
                };

                // Calculate total for percentage
                const totalClinics = Object.values(data).reduce(
                  (sum: number, val: any) => sum + (typeof val === 'number' ? val : 0),
                  0
                );

                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {statusCategories.map((status, index) => {
                      const count = data[status.key] || 0;
                      const percentage =
                        totalClinics > 0 ? ((count / totalClinics) * 100).toFixed(1) : '0.0';
                      const IconComponent =
                        iconComponents[status.iconName as keyof typeof iconComponents];

                      return (
                        <motion.div
                          key={status.label}
                          initial={{ opacity: 0, y: 30, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{
                            delay: 0.8 + index * 0.1,
                            type: 'spring',
                            stiffness: 100,
                          }}
                          className="transform hover:scale-105 transition-all duration-300"
                        >
                          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 h-full flex flex-col">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2.5">
                                <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                                  <IconComponent
                                    className="w-4 h-4"
                                    style={{ color: status.color }}
                                    strokeWidth={2.5}
                                  />
                                </div>
                                <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                  {status.label}
                                </h5>
                              </div>
                              <span
                                className="text-sm font-semibold"
                                style={{ color: status.color }}
                              >
                                {percentage}%
                              </span>
                            </div>
                            <div className="flex items-baseline gap-2 mt-auto">
                              <span className="text-3xl font-bold" style={{ color: status.color }}>
                                {count}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                clinics
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                );
              })()}
            </motion.div>

            {/* Average Days in Onboarding Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-cyan-50/30 to-blue-50/30 dark:from-gray-800/50 dark:via-cyan-900/20 dark:to-blue-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.7, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2 flex items-center gap-3">
                  <Clock className="w-8 h-8 text-cyan-600" />
                  Average Days in Onboarding
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Average number of days spent in onboarding for closed clinics
                </p>
              </motion.div>

              {/* Average Days Metric Card */}
              <div className="grid grid-cols-1 gap-6">
                <div className="flex justify-center">
                  {(() => {
                    const avgDaysMetric = allMetrics.find(m => m.id === 'avg-days-in-onboarding');

                    if (avgDaysMetric) {
                      return (
                        <motion.div
                          key="avg-days-in-onboarding"
                          initial={{ opacity: 0, y: 30, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ delay: 0.9, type: 'spring', stiffness: 100 }}
                          className="transform hover:scale-105 transition-all duration-300 w-full"
                        >
                          <MetricCard metric={avgDaysMetric} />
                        </motion.div>
                      );
                    }

                    return null;
                  })()}
                </div>
              </div>
            </motion.div>

            {/* Onboarding Health Metrics Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-cyan-50/30 to-teal-50/30 dark:from-gray-800/50 dark:via-cyan-900/20 dark:to-teal-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.7, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 via-teal-600 to-emerald-600 bg-clip-text text-transparent mb-2 flex items-center gap-3">
                  <BarChart3 className="w-8 h-8 text-cyan-600" />
                  Onboarding Health Metrics
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Key performance indicators to measure onboarding effectiveness and health
                </p>
              </motion.div>

              {/* Health Metrics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Essentials Onboarding Health Score */}
                {(() => {
                  const essentialsHealthMetric = allMetrics.find(
                    m => m.id === 'essentials-health-onboarding'
                  );

                  if (
                    !essentialsHealthMetric ||
                    !essentialsHealthMetric.value ||
                    typeof essentialsHealthMetric.value !== 'object'
                  ) {
                    return null;
                  }

                  const data = essentialsHealthMetric.value as Record<string, number>;
                  // Handle both lowercase and capitalized keys
                  const green = data.green || data.Green || 0;
                  const yellow = data.yellow || data.Yellow || 0;
                  const red = data.red || data.Red || 0;
                  const total = green + yellow + red;
                  const healthyPercentage =
                    total > 0 ? (((green + yellow) / total) * 100).toFixed(1) : '0.0';

                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 30, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 0.9, type: 'spring', stiffness: 100 }}
                      className="transform hover:scale-105 transition-all duration-300"
                    >
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 h-full flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2.5">
                            <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30">
                              <CheckCircle className="w-4 h-4 text-emerald-600" strokeWidth={2.5} />
                            </div>
                            <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              Essentials Onboarding Health Score
                            </h5>
                            <MetricInfoTooltip
                              info={essentialsHealthMetric.info}
                              query={essentialsHealthMetric.query}
                            />
                          </div>
                        </div>
                        <div className="flex items-baseline gap-2 mt-auto">
                          <span className="text-3xl font-bold text-emerald-600">
                            {healthyPercentage}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          ({green + yellow} healthy / {total} total)
                        </p>
                      </div>
                    </motion.div>
                  );
                })()}

                {/* EHR Onboarding Health Score */}
                {(() => {
                  const ehrHealthMetric = allMetrics.find(
                    m => m.id === 'ehr-onboarding-health-score'
                  );

                  if (
                    !ehrHealthMetric ||
                    !ehrHealthMetric.value ||
                    typeof ehrHealthMetric.value !== 'object'
                  ) {
                    return null;
                  }

                  const data = ehrHealthMetric.value as Record<string, number>;
                  // Handle both lowercase and capitalized keys
                  const green = data.green || data.Green || 0;
                  const yellow = data.yellow || data.Yellow || 0;
                  const red = data.red || data.Red || 0;
                  const total = green + yellow + red;
                  const healthyPercentage =
                    total > 0 ? (((green + yellow) / total) * 100).toFixed(1) : '0.0';

                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 30, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 1.0, type: 'spring', stiffness: 100 }}
                      className="transform hover:scale-105 transition-all duration-300"
                    >
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 h-full flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2.5">
                            <div className="p-2 rounded-lg bg-cyan-50 dark:bg-cyan-900/30">
                              <CheckCircle className="w-4 h-4 text-cyan-600" strokeWidth={2.5} />
                            </div>
                            <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              EHR Onboarding Health Score
                            </h5>
                            <MetricInfoTooltip
                              info={ehrHealthMetric.info}
                              query={ehrHealthMetric.query}
                            />
                          </div>
                        </div>
                        <div className="flex items-baseline gap-2 mt-auto">
                          <span className="text-3xl font-bold text-cyan-600">
                            {healthyPercentage}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          ({green + yellow} healthy / {total} total)
                        </p>
                      </div>
                    </motion.div>
                  );
                })()}
              </div>
            </motion.div>
          </Tabs.Content>

          {/* Funnel Tab Content */}
          <Tabs.Content value="funnel" className="focus:outline-none">
            {(() => {
              const funnelMetric = allMetrics.find(m => m.id === 'onboarding-funnel-stages');
              const churnMetric = allMetrics.find(m => m.id === 'onboarding-churn-breakdown');

              if (
                funnelMetric &&
                Array.isArray(funnelMetric.value) &&
                funnelMetric.value.length > 0
              ) {
                return (
                  <FunnelChart
                    data={funnelMetric.value as Array<{ stageName: string; count: number }>}
                    churnData={
                      churnMetric && Array.isArray(churnMetric.value)
                        ? (churnMetric.value as Array<{ type: string; count: number }>)
                        : undefined
                    }
                    info={funnelMetric.info}
                    query={funnelMetric.query}
                    churnInfo={churnMetric?.info}
                    churnQuery={churnMetric?.query}
                  />
                );
              }

              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex items-center justify-center min-h-[400px] bg-white dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 p-12"
                >
                  <div className="text-center">
                    <Filter className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      No Funnel Data Available
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-md">
                      Funnel data will appear here once clinics enter the onboarding pipeline.
                    </p>
                  </div>
                </motion.div>
              );
            })()}
          </Tabs.Content>

          {/* Visualizations Tab Content */}
          <Tabs.Content value="visualizations" className="focus:outline-none">
            {/* Coach Distribution Charts - Centered */}
            <div className="max-w-6xl mx-auto mb-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Active Onboarding by Coach Section */}
                <motion.div
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/20 rounded-xl shadow-md border border-blue-200 dark:border-blue-800/30">
                      <Users
                        className="w-6 h-6 text-blue-600 dark:text-blue-400"
                        strokeWidth={2.5}
                      />
                    </div>
                    <h3 className="text-lg font-bold text-blue-600 dark:text-blue-400">
                      Current Active Onboarding by Coach
                    </h3>
                    <MetricInfoTooltip
                      info={allMetrics.find(m => m.id === 'active-onboarding-by-coach')?.info}
                      query={allMetrics.find(m => m.id === 'active-onboarding-by-coach')?.query}
                    />
                  </div>

                  {/* Coach Distribution Donut Chart */}
                  <div className="flex justify-center">
                    {(() => {
                      const coachMetric = allMetrics.find(
                        m => m.id === 'active-onboarding-by-coach'
                      );

                      if (isLoading || isFetching) {
                        return (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                          </div>
                        );
                      }

                      if (!coachMetric) {
                        return (
                          <div className="flex items-center justify-center py-12 text-red-600">
                            <AlertCircle className="w-6 h-6 mr-2" />
                            <span>Error loading coach distribution</span>
                          </div>
                        );
                      }

                      const data = coachMetric.value;

                      if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
                        return (
                          <div className="flex items-center justify-center py-12 text-gray-500">
                            <AlertCircle className="w-6 h-6 mr-2" />
                            <span>No active onboarding clinics found</span>
                          </div>
                        );
                      }

                      // Transform data for donut chart
                      const chartData = Object.entries(data).map(([coach, count], index) => ({
                        name: coach || 'Unassigned',
                        value: count as number,
                        color: [
                          '#8B5CF6', // Purple
                          '#6366F1', // Indigo
                          '#3B82F6', // Blue
                          '#06B6D4', // Cyan
                          '#10B981', // Green
                          '#F59E0B', // Amber
                          '#EF4444', // Red
                          '#EC4899', // Pink
                        ][index % 8],
                      }));

                      const total = chartData.reduce((sum, item) => sum + item.value, 0);

                      // Calculate tooltip position based on segment angle
                      const getTooltipPosition = (index: number) => {
                        let currentAngle = -90;

                        for (let i = 0; i < index; i++) {
                          const percentage = chartData[i].value / total;
                          currentAngle += percentage * 360;
                        }

                        const segmentPercentage = chartData[index].value / total;
                        const segmentAngle = segmentPercentage * 360;
                        const middleAngle = currentAngle + segmentAngle / 2;
                        const angleRad = (middleAngle * Math.PI) / 180;

                        const distance = 145;
                        const centerX = 170;
                        const centerY = 170;

                        const x = centerX + distance * Math.cos(angleRad);
                        const y = centerY + distance * Math.sin(angleRad);

                        return { x, y };
                      };

                      const tooltipPos =
                        hoveredCoach1Index !== null
                          ? getTooltipPosition(hoveredCoach1Index)
                          : { x: 0, y: 0 };

                      return (
                        <div className="flex flex-col items-center">
                          {/* Donut Chart */}
                          <div className="relative">
                            <svg viewBox="0 0 340 340" className="w-full max-w-[280px] mb-6">
                              <DonutSegments
                                data={chartData}
                                total={total}
                                onSegmentHover={setHoveredCoach1Index}
                                hoveredIndex={hoveredCoach1Index}
                              />
                              <text
                                x="170"
                                y="160"
                                textAnchor="middle"
                                className="text-3xl font-bold fill-gray-900 dark:fill-gray-100"
                              >
                                {total}
                              </text>
                              <text
                                x="170"
                                y="190"
                                textAnchor="middle"
                                className="text-xs fill-gray-500 dark:fill-gray-400"
                              >
                                TOTAL
                              </text>
                            </svg>

                            {/* Tooltip */}
                            <AnimatePresence>
                              {hoveredCoach1Index !== null && (
                                <motion.div
                                  className="absolute bg-white dark:bg-gray-800 text-gray-800 dark:text-white px-3 py-2 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 pointer-events-none z-50"
                                  style={{
                                    left: `${tooltipPos.x}px`,
                                    top: `${tooltipPos.y}px`,
                                    transform: 'translate(-50%, -50%)',
                                    minWidth: '120px',
                                  }}
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.9 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <div className="text-center">
                                    <div className="text-xs font-semibold mb-1">
                                      {chartData[hoveredCoach1Index].name}
                                    </div>
                                    <div className="text-lg font-bold">
                                      {chartData[hoveredCoach1Index].value}
                                    </div>
                                    <div className="text-xs opacity-70">
                                      {(
                                        (chartData[hoveredCoach1Index].value / total) *
                                        100
                                      ).toFixed(1)}
                                      %
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>

                          {/* Legend */}
                          <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-4 border border-gray-200/60 dark:border-gray-700/60 shadow-lg backdrop-blur-sm">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {chartData.map((item, index) => (
                                <motion.div
                                  key={index}
                                  className="group flex items-center space-x-3 p-3 bg-white/80 dark:bg-gray-800/80 rounded-lg border border-gray-100/80 dark:border-gray-700/80 hover:border-blue-200 dark:hover:border-blue-600/50 hover:shadow-md transition-all duration-200"
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.6 + index * 0.1, duration: 0.3 }}
                                  whileHover={{ scale: 1.02 }}
                                >
                                  {/* Color Indicator with Glow */}
                                  <div className="relative flex-shrink-0">
                                    <div
                                      className="w-3.5 h-3.5 rounded-full shadow-md border border-white/80 dark:border-gray-600/80"
                                      style={{ backgroundColor: item.color }}
                                    />
                                    <div
                                      className="absolute inset-0 w-3.5 h-3.5 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-200 blur-sm"
                                      style={{ backgroundColor: item.color }}
                                    />
                                  </div>

                                  {/* Content */}
                                  <div className="flex flex-col items-start min-w-0 flex-1">
                                    <span className="text-gray-900 dark:text-gray-100 font-medium text-sm leading-tight truncate w-full">
                                      {item.name}
                                    </span>
                                    <div className="flex items-center space-x-1.5 mt-0.5">
                                      <span className="text-gray-700 dark:text-gray-300 text-xs font-semibold">
                                        {item.value}
                                      </span>
                                      <span className="text-gray-500 dark:text-gray-400 text-xs">
                                        clinics
                                      </span>
                                      <span className="text-blue-600 dark:text-blue-400 text-xs font-semibold">
                                        ({((item.value / total) * 100).toFixed(1)}%)
                                      </span>
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </motion.div>

                {/* Total Onboardings Done by Coach (All Stages) */}
                <motion.div
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <div className="p-3 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/20 rounded-xl shadow-md border border-orange-200 dark:border-orange-800/30">
                      <BarChart3
                        className="w-6 h-6 text-orange-600 dark:text-orange-400"
                        strokeWidth={2.5}
                      />
                    </div>
                    <h3 className="text-lg font-bold text-orange-600 dark:text-orange-400">
                      Total Onboardings Done by Coach
                    </h3>
                    <MetricInfoTooltip
                      info={allMetrics.find(m => m.id === 'total-onboardings-by-coach')?.info}
                      query={allMetrics.find(m => m.id === 'total-onboardings-by-coach')?.query}
                    />
                  </div>

                  {/* Total Onboardings by Coach Donut Chart */}
                  <div className="flex justify-center">
                    {(() => {
                      const totalCoachMetric = allMetrics.find(
                        m => m.id === 'total-onboardings-by-coach'
                      );

                      if (isLoading || isFetching) {
                        return (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                          </div>
                        );
                      }

                      if (!totalCoachMetric) {
                        return (
                          <div className="flex items-center justify-center py-12 text-red-600">
                            <AlertCircle className="w-6 h-6 mr-2" />
                            <span>Error loading total onboardings by coach</span>
                          </div>
                        );
                      }

                      const data = totalCoachMetric.value;

                      if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
                        return (
                          <div className="flex items-center justify-center py-12 text-gray-500">
                            <AlertCircle className="w-6 h-6 mr-2" />
                            <span>No onboarding clinics found</span>
                          </div>
                        );
                      }

                      // Transform data for donut chart
                      const chartData = Object.entries(data).map(([coach, count], index) => ({
                        name: coach || 'Unassigned',
                        value: count as number,
                        color: [
                          '#10B981', // Emerald
                          '#059669', // Green
                          '#14B8A6', // Teal
                          '#06B6D4', // Cyan
                          '#0EA5E9', // Sky
                          '#3B82F6', // Blue
                          '#6366F1', // Indigo
                          '#8B5CF6', // Purple
                        ][index % 8],
                      }));

                      const total = chartData.reduce((sum, item) => sum + item.value, 0);

                      // Calculate tooltip position based on segment angle
                      const getTooltipPosition2 = (index: number) => {
                        let currentAngle = -90;

                        for (let i = 0; i < index; i++) {
                          const percentage = chartData[i].value / total;
                          currentAngle += percentage * 360;
                        }

                        const segmentPercentage = chartData[index].value / total;
                        const segmentAngle = segmentPercentage * 360;
                        const middleAngle = currentAngle + segmentAngle / 2;
                        const angleRad = (middleAngle * Math.PI) / 180;

                        const distance = 145;
                        const centerX = 170;
                        const centerY = 170;

                        const x = centerX + distance * Math.cos(angleRad);
                        const y = centerY + distance * Math.sin(angleRad);

                        return { x, y };
                      };

                      const tooltipPos2 =
                        hoveredCoach2Index !== null
                          ? getTooltipPosition2(hoveredCoach2Index)
                          : { x: 0, y: 0 };

                      return (
                        <div className="flex flex-col items-center">
                          {/* Donut Chart */}
                          <div className="relative">
                            <svg viewBox="0 0 340 340" className="w-full max-w-[280px] mb-6">
                              <DonutSegments
                                data={chartData}
                                total={total}
                                onSegmentHover={setHoveredCoach2Index}
                                hoveredIndex={hoveredCoach2Index}
                              />
                              <text
                                x="170"
                                y="160"
                                textAnchor="middle"
                                className="text-3xl font-bold fill-gray-900 dark:fill-gray-100"
                              >
                                {total}
                              </text>
                              <text
                                x="170"
                                y="190"
                                textAnchor="middle"
                                className="text-xs fill-gray-500 dark:fill-gray-400"
                              >
                                TOTAL
                              </text>
                            </svg>

                            {/* Tooltip */}
                            <AnimatePresence>
                              {hoveredCoach2Index !== null && (
                                <motion.div
                                  className="absolute bg-white dark:bg-gray-800 text-gray-800 dark:text-white px-3 py-2 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 pointer-events-none z-50"
                                  style={{
                                    left: `${tooltipPos2.x}px`,
                                    top: `${tooltipPos2.y}px`,
                                    transform: 'translate(-50%, -50%)',
                                    minWidth: '120px',
                                  }}
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.9 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <div className="text-center">
                                    <div className="text-xs font-semibold mb-1">
                                      {chartData[hoveredCoach2Index].name}
                                    </div>
                                    <div className="text-lg font-bold">
                                      {chartData[hoveredCoach2Index].value}
                                    </div>
                                    <div className="text-xs opacity-70">
                                      {(
                                        (chartData[hoveredCoach2Index].value / total) *
                                        100
                                      ).toFixed(1)}
                                      %
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>

                          {/* Legend */}
                          <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-4 border border-gray-200/60 dark:border-gray-700/60 shadow-lg backdrop-blur-sm">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {chartData.map((item, index) => (
                                <motion.div
                                  key={index}
                                  className="group flex items-center space-x-3 p-3 bg-white/80 dark:bg-gray-800/80 rounded-lg border border-gray-100/80 dark:border-gray-700/80 hover:border-orange-200 dark:hover:border-orange-600/50 hover:shadow-md transition-all duration-200"
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.6 + index * 0.1, duration: 0.3 }}
                                  whileHover={{ scale: 1.02 }}
                                >
                                  {/* Color Indicator with Glow */}
                                  <div className="relative flex-shrink-0">
                                    <div
                                      className="w-3.5 h-3.5 rounded-full shadow-md border border-white/80 dark:border-gray-600/80"
                                      style={{ backgroundColor: item.color }}
                                    />
                                    <div
                                      className="absolute inset-0 w-3.5 h-3.5 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-200 blur-sm"
                                      style={{ backgroundColor: item.color }}
                                    />
                                  </div>

                                  {/* Content */}
                                  <div className="flex flex-col items-start min-w-0 flex-1">
                                    <span className="text-gray-900 dark:text-gray-100 font-medium text-sm leading-tight truncate w-full">
                                      {item.name}
                                    </span>
                                    <div className="flex items-center space-x-1.5 mt-0.5">
                                      <span className="text-gray-700 dark:text-gray-300 text-xs font-semibold">
                                        {item.value}
                                      </span>
                                      <span className="text-gray-500 dark:text-gray-400 text-xs">
                                        clinics
                                      </span>
                                      <span className="text-orange-600 dark:text-orange-400 text-xs font-semibold">
                                        ({((item.value / total) * 100).toFixed(1)}%)
                                      </span>
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Health Score Section - Essentials Onboarding Health */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-cyan-50/30 to-blue-50/30 dark:from-gray-800/50 dark:via-cyan-900/20 dark:to-blue-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.4, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="mb-8"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent flex items-center gap-3">
                  <Target className="w-8 h-8 text-cyan-600" />
                  Essentials Onboarding Health
                </h3>
              </motion.div>

              {/* Health Score Donut Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
                {/* Essentials Onboarding Health Score */}
                {(() => {
                  const healthMetric = allMetrics.find(
                    m => m.id === 'essentials-health-onboarding'
                  );

                  if (
                    healthMetric &&
                    typeof healthMetric.value === 'object' &&
                    healthMetric.value !== null &&
                    'green' in healthMetric.value
                  ) {
                    return (
                      <motion.div
                        key="essentials-health-onboarding"
                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.6, type: 'spring', stiffness: 100 }}
                        className="transform hover:scale-105 transition-all duration-300"
                      >
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 h-full">
                          {/* Enhanced Title */}
                          <div className="flex items-center justify-center gap-3 mb-6">
                            <div className="p-3 bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/30 dark:to-cyan-800/20 rounded-xl shadow-md border border-cyan-200 dark:border-cyan-800/30">
                              <Activity
                                className="w-6 h-6 text-cyan-600 dark:text-cyan-400"
                                strokeWidth={2.5}
                              />
                            </div>
                            <h3 className="text-lg font-bold text-cyan-700 dark:text-cyan-400">
                              Essentials Onboarding Health Score
                            </h3>
                            <MetricInfoTooltip
                              info={healthMetric.info}
                              query={healthMetric.query}
                            />
                          </div>
                          <DonutChart
                            data={
                              healthMetric.value as { green: number; yellow: number; red: number }
                            }
                            title=""
                          />
                        </div>
                      </motion.div>
                    );
                  }

                  return (
                    <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 w-full">
                      <div className="text-center">
                        <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">
                          No Essentials health data
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* EHR Onboarding Health Score */}
                {(() => {
                  const ehrHealthMetric = allMetrics.find(
                    m => m.id === 'ehr-onboarding-health-score'
                  );

                  if (
                    ehrHealthMetric &&
                    typeof ehrHealthMetric.value === 'object' &&
                    ehrHealthMetric.value !== null
                  ) {
                    const rawData = ehrHealthMetric.value as { [key: string]: number };

                    // Map Usage_Health__c values to green/yellow/red format
                    const healthData = {
                      green: rawData['Green'] || 0,
                      yellow: rawData['Yellow'] || 0,
                      red: rawData['Red'] || 0,
                    };

                    const total = healthData.green + healthData.yellow + healthData.red;

                    if (total > 0) {
                      return (
                        <motion.div
                          key="ehr-onboarding-health-score"
                          initial={{ opacity: 0, y: 30, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ delay: 0.7, type: 'spring', stiffness: 100 }}
                          className="transform hover:scale-105 transition-all duration-300"
                        >
                          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 h-full">
                            {/* Enhanced Title */}
                            <div className="flex items-center justify-center gap-3 mb-6">
                              <div className="p-3 bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/30 dark:to-cyan-800/20 rounded-xl shadow-md border border-cyan-200 dark:border-cyan-800/30">
                                <Activity
                                  className="w-6 h-6 text-cyan-600 dark:text-cyan-400"
                                  strokeWidth={2.5}
                                />
                              </div>
                              <h3 className="text-lg font-bold text-cyan-700 dark:text-cyan-400">
                                EHR Onboarding Health Score
                              </h3>
                              <MetricInfoTooltip
                                info={ehrHealthMetric.info}
                                query={ehrHealthMetric.query}
                              />
                            </div>
                            <DonutChart data={healthData} title="" />
                          </div>
                        </motion.div>
                      );
                    }
                  }

                  return (
                    <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 w-full">
                      <div className="text-center">
                        <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">No EHR health data</p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </motion.div>

            {/* Time to Go-Live Distribution */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, type: 'spring', stiffness: 80 }}
              className="mt-8"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-xl hover:shadow-2xl transition-all duration-300">
                {(() => {
                  const timeMetric = allMetrics.find(m => m.id === 'time-to-go-live-distribution');

                  if (isLoading || isFetching) {
                    return (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                      </div>
                    );
                  }

                  if (!timeMetric || typeof timeMetric.value !== 'object' || !timeMetric.value) {
                    return (
                      <div className="flex items-center justify-center py-12 text-gray-500">
                        <AlertCircle className="w-6 h-6 mr-2" />
                        <span>No onboarding duration data available</span>
                      </div>
                    );
                  }

                  const timeData = timeMetric.value as {
                    min: number;
                    max: number;
                    avg: number;
                    median: number;
                    percentile25: number;
                    percentile75: number;
                    percentile90: number;
                    total: number;
                    quartileCounts?: {
                      q1: number;
                      q2: number;
                      q3: number;
                      q4: number;
                    };
                  };

                  if (timeData.total === 0) {
                    return (
                      <div className="flex items-center justify-center py-12 text-gray-500">
                        <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">
                          No completed onboardings in selected period
                        </p>
                      </div>
                    );
                  }

                  return (
                    <TimeToGoLiveCard
                      data={timeData}
                      title="Time to Go-Live Distribution"
                      info={timeMetric.info}
                      query={timeMetric.query}
                    />
                  );
                })()}
              </div>
            </motion.div>

            {/* Onboarding Trend Over Time */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, type: 'spring', stiffness: 80 }}
              className="mt-8"
            >
              {(() => {
                const trendMetric = allMetrics.find(m => m.id === 'onboarding-trend-over-time');

                if (isLoading || isFetching) {
                  return (
                    <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                      <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                    </div>
                  );
                }

                if (
                  !trendMetric ||
                  typeof trendMetric.value !== 'object' ||
                  !trendMetric.value ||
                  !(trendMetric.value as any).data
                ) {
                  return (
                    <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                      <div className="text-center">
                        <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">No trend data available</p>
                      </div>
                    </div>
                  );
                }

                const trendData = (trendMetric.value as any).data;

                return (
                  <TrendLineChart
                    data={trendData}
                    title="Onboarding Trend Over Time"
                    info={trendMetric.info}
                    query={trendMetric.query}
                  />
                );
              })()}
            </motion.div>
          </Tabs.Content>

          {/* Leaderboard Tab Content */}
          <Tabs.Content value="leaderboard" className="focus:outline-none">
            {(() => {
              // Get the active onboarding by coach metric for leaderboard
              const activeOnboardingMetric = allMetrics.find(
                m => m.id === 'active-onboarding-by-coach'
              );

              // Get the total onboardings (all time) metric
              const totalOnboardingsMetric = allMetrics.find(
                m => m.id === 'total-onboardings-by-coach-all-time'
              );

              // Get the average days by coach metric
              const avgDaysMetric = allMetrics.find(m => m.id === 'avg-days-by-coach-all-time');

              // Get success rate metrics
              const successRateWonMetric = allMetrics.find(
                m => m.id === 'success-rate-won-by-coach'
              );
              const successRateTotalMetric = allMetrics.find(
                m => m.id === 'success-rate-total-by-coach'
              );

              // Get checklist completion rate metrics
              const checklistCompleteMetric = allMetrics.find(
                m => m.id === 'checklist-complete-by-coach'
              );
              const checklistTotalMetric = allMetrics.find(
                m => m.id === 'checklist-total-by-coach'
              );

              if (!activeOnboardingMetric || !activeOnboardingMetric.value) {
                return (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center justify-center min-h-[400px] bg-white dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 p-12"
                  >
                    <div className="text-center">
                      <Trophy className="w-16 h-16 text-amber-400 mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        No Leaderboard Data Available
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Onboarding performance rankings will appear here when data is available
                      </p>
                    </div>
                  </motion.div>
                );
              }

              // Get all onboarding coaches from all metrics
              const coachData = activeOnboardingMetric.value as { [key: string]: number };
              const totalCoachData =
                (totalOnboardingsMetric?.value as { [key: string]: number }) || {};
              const avgDaysData = (avgDaysMetric?.value as { [key: string]: number }) || {};
              const successWonData =
                (successRateWonMetric?.value as { [key: string]: number }) || {};
              const successTotalData =
                (successRateTotalMetric?.value as { [key: string]: number }) || {};
              const checklistCompleteData =
                (checklistCompleteMetric?.value as { [key: string]: number }) || {};
              const checklistTotalData =
                (checklistTotalMetric?.value as { [key: string]: number }) || {};

              // Combine all unique coaches from all metrics
              const allCoaches = new Set([
                ...Object.keys(coachData),
                ...Object.keys(totalCoachData),
                ...Object.keys(avgDaysData),
                ...Object.keys(successWonData),
                ...Object.keys(successTotalData),
                ...Object.keys(checklistCompleteData),
                ...Object.keys(checklistTotalData),
              ]);

              // Sort by active onboardings (primary), then by total onboardings (secondary)
              const sortedCoaches = Array.from(allCoaches).sort((a, b) => {
                const activeA = coachData[a] || 0;
                const activeB = coachData[b] || 0;

                // Primary sort: by active onboardings (descending)
                if (activeB !== activeA) {
                  return activeB - activeA;
                }

                // Secondary sort: by total onboardings (descending) if active are equal
                const totalA = totalCoachData[a] || 0;
                const totalB = totalCoachData[b] || 0;

                return totalB - totalA;
              });

              // Color schemes for rows (same as Support Dashboard)
              const getRowColorScheme = (idx: number) => {
                const schemes = [
                  {
                    light: 'bg-gradient-to-r from-yellow-50/80 via-amber-50/60 to-orange-50/40',
                    dark: 'dark:bg-gradient-to-r dark:from-yellow-900/20 dark:via-amber-900/15 dark:to-orange-900/10',
                  }, // Gold
                  {
                    light: 'bg-gradient-to-r from-gray-50/80 via-slate-50/60 to-zinc-50/40',
                    dark: 'dark:bg-gradient-to-r dark:from-gray-800/20 dark:via-slate-800/15 dark:to-zinc-800/10',
                  }, // Silver
                  {
                    light: 'bg-gradient-to-r from-amber-50/80 via-orange-50/60 to-yellow-50/40',
                    dark: 'dark:bg-gradient-to-r dark:from-amber-900/20 dark:via-orange-900/15 dark:to-yellow-900/10',
                  }, // Bronze
                  {
                    light: 'bg-gradient-to-r from-blue-50/80 via-indigo-50/60 to-purple-50/40',
                    dark: 'dark:bg-gradient-to-r dark:from-blue-900/20 dark:via-indigo-900/15 dark:to-purple-900/10',
                  },
                  {
                    light: 'bg-gradient-to-r from-green-50/80 via-emerald-50/60 to-teal-50/40',
                    dark: 'dark:bg-gradient-to-r dark:from-green-900/20 dark:via-emerald-900/15 dark:to-teal-900/10',
                  },
                ];

                return schemes[idx % schemes.length];
              };

              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                        <Trophy className="w-8 h-8 text-amber-500" />
                        Onboarding Coach Leaderboard
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Ranked by active onboardings in the selected period
                      </p>
                    </div>
                  </div>

                  {/* Leaderboard Table */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {/* Monday.com Style Table Container */}
                    <div className="relative">
                      {/* Fixed Columns Container (Rank + Coach Name) */}
                      <div className="absolute left-0 top-0 z-20 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
                        {/* Fixed Header */}
                        <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                          <div className="flex h-20">
                            {/* Rank Column Header */}
                            <motion.div
                              className="w-24 px-3 py-3 border-r border-gray-300 dark:border-gray-500 flex flex-col items-center justify-center gap-1 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 hover:from-amber-50 hover:to-amber-100 dark:hover:from-amber-900/20 dark:hover:to-amber-800/20 transition-all duration-200"
                              transition={{ duration: 0.2 }}
                            >
                              <motion.div
                                className="p-1 rounded-md bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600"
                                whileHover={{ rotate: 5, scale: 1.05 }}
                                transition={{ duration: 0.15 }}
                              >
                                <div className="text-amber-600 dark:text-amber-400">
                                  <Trophy className="w-3.5 h-3.5" />
                                </div>
                              </motion.div>
                              <div className="text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide leading-tight">
                                Rank
                              </div>
                            </motion.div>
                            {/* Coach Name Column Header */}
                            <motion.div
                              className="w-52 px-3 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide flex flex-col items-center justify-center gap-1.5 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 hover:from-amber-50 hover:to-amber-100 dark:hover:from-amber-900/20 dark:hover:to-amber-800/20 transition-all duration-200"
                              transition={{ duration: 0.2 }}
                            >
                              <motion.div
                                className="p-1 rounded-md bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600"
                                whileHover={{ rotate: 5, scale: 1.05 }}
                                transition={{ duration: 0.15 }}
                              >
                                <div className="text-amber-600 dark:text-amber-400">
                                  <Users className="w-3.5 h-3.5" />
                                </div>
                              </motion.div>
                              <div className="truncate text-center leading-tight">
                                Onboarding Coach
                              </div>
                            </motion.div>
                          </div>
                        </div>

                        {/* Fixed Rows */}
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {sortedCoaches.map((coach, index) => {
                            const colorScheme = getRowColorScheme(index);

                            return (
                              <motion.div
                                key={coach}
                                className={`group flex relative border-b border-gray-200 dark:border-gray-700 transition-all duration-300`}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05, duration: 0.4 }}
                              >
                                {/* Rank Column */}
                                <div className="w-24 px-3 py-3 border-r border-gray-300 dark:border-gray-500 flex items-center justify-center h-14">
                                  {index === 0 && (
                                    <motion.div
                                      className="relative"
                                      whileHover={{ scale: 1.05, rotate: 3 }}
                                      transition={{ type: 'spring', stiffness: 350, damping: 22 }}
                                    >
                                      <div className="w-9 h-9 bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-md border border-yellow-200">
                                        <Trophy className="w-4 h-4 text-yellow-800" />
                                      </div>
                                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-br from-yellow-200 to-yellow-400 rounded-full animate-pulse"></div>
                                    </motion.div>
                                  )}
                                  {index === 1 && (
                                    <motion.div
                                      whileHover={{ scale: 1.05 }}
                                      transition={{ type: 'spring', stiffness: 350, damping: 22 }}
                                    >
                                      <div className="w-9 h-9 bg-gradient-to-br from-gray-200 via-gray-300 to-gray-400 rounded-full flex items-center justify-center shadow-md border border-gray-300">
                                        <Trophy className="w-4 h-4 text-gray-600" />
                                      </div>
                                    </motion.div>
                                  )}
                                  {index === 2 && (
                                    <motion.div
                                      whileHover={{ scale: 1.05 }}
                                      transition={{ type: 'spring', stiffness: 350, damping: 22 }}
                                    >
                                      <div className="w-9 h-9 bg-gradient-to-br from-amber-300 via-orange-400 to-amber-500 rounded-full flex items-center justify-center shadow-md border border-amber-300">
                                        <Trophy className="w-4 h-4 text-amber-800" />
                                      </div>
                                    </motion.div>
                                  )}
                                  {index > 2 && (
                                    <motion.div
                                      className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center shadow-sm border border-slate-200 dark:border-slate-600"
                                      whileHover={{ scale: 1.08 }}
                                      transition={{ type: 'spring', stiffness: 350, damping: 22 }}
                                    >
                                      <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                                        {index + 1}
                                      </span>
                                    </motion.div>
                                  )}
                                </div>

                                {/* Coach Name Column */}
                                <div className="w-52 px-3 py-3 flex items-center justify-center h-14">
                                  <motion.div
                                    className="text-sm font-bold text-slate-800 dark:text-white truncate text-center group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors duration-300"
                                    whileHover={{ scale: 1.02 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                  >
                                    {coach}
                                  </motion.div>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Scrollable Metrics Container */}
                      <div className="overflow-x-auto pl-[19rem]">
                        <div className="inline-block min-w-full">
                          {/* Scrollable Header */}
                          <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                            <div className="flex h-20">
                              {/* Active Onboardings Column Header */}
                              <motion.div
                                className="w-44 px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide flex flex-col items-center justify-center gap-1.5 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 hover:from-green-50 hover:to-green-100 dark:hover:from-green-900/20 dark:hover:to-green-800/20 transition-all duration-200 border-l border-gray-200 dark:border-gray-600"
                                transition={{ duration: 0.2 }}
                              >
                                <motion.div
                                  className="p-1 rounded-md bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600"
                                  whileHover={{ rotate: 5, scale: 1.05 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <div className="text-green-600 dark:text-green-400">
                                    <UserPlus className="w-3.5 h-3.5" />
                                  </div>
                                </motion.div>
                                <div className="flex items-center gap-1">
                                  <div className="truncate text-center leading-tight">
                                    Active Onboardings
                                  </div>
                                  <MetricInfoTooltip
                                    info={activeOnboardingMetric.info}
                                    query={activeOnboardingMetric.query}
                                  />
                                </div>
                              </motion.div>
                              {/* Total Onboardings Column Header */}
                              <motion.div
                                className="w-44 px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide flex flex-col items-center justify-center gap-1.5 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 hover:from-amber-50 hover:to-amber-100 dark:hover:from-amber-900/20 dark:hover:to-amber-800/20 transition-all duration-200 border-l border-gray-200 dark:border-gray-600"
                                transition={{ duration: 0.2 }}
                              >
                                <motion.div
                                  className="p-1 rounded-md bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600"
                                  whileHover={{ rotate: 5, scale: 1.05 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <div className="text-amber-600 dark:text-amber-400">
                                    <CheckCircle className="w-3.5 h-3.5" />
                                  </div>
                                </motion.div>
                                <div className="flex items-center gap-1">
                                  <div className="truncate text-center leading-tight">
                                    Total Onboardings
                                  </div>
                                  <MetricInfoTooltip
                                    info={totalOnboardingsMetric?.info}
                                    query={totalOnboardingsMetric?.query}
                                  />
                                </div>
                              </motion.div>
                              {/* Avg Days Column Header */}
                              <motion.div
                                className="w-44 px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide flex flex-col items-center justify-center gap-1.5 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 hover:from-cyan-50 hover:to-cyan-100 dark:hover:from-cyan-900/20 dark:hover:to-cyan-800/20 transition-all duration-200 border-l border-gray-200 dark:border-gray-600"
                                transition={{ duration: 0.2 }}
                              >
                                <motion.div
                                  className="p-1 rounded-md bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600"
                                  whileHover={{ rotate: 5, scale: 1.05 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <div className="text-cyan-600 dark:text-cyan-400">
                                    <Filter className="w-3.5 h-3.5" />
                                  </div>
                                </motion.div>
                                <div className="flex items-center gap-1">
                                  <div className="truncate text-center leading-tight">Avg Days</div>
                                  <MetricInfoTooltip
                                    info={avgDaysMetric?.info}
                                    query={avgDaysMetric?.query}
                                  />
                                </div>
                              </motion.div>
                              {/* Success Rate Column Header */}
                              <motion.div
                                className="w-44 px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide flex flex-col items-center justify-center gap-1.5 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 hover:from-green-50 hover:to-green-100 dark:hover:from-green-900/20 dark:hover:to-green-800/20 transition-all duration-200 border-l border-gray-200 dark:border-gray-600"
                                transition={{ duration: 0.2 }}
                              >
                                <motion.div
                                  className="p-1 rounded-md bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600"
                                  whileHover={{ rotate: 5, scale: 1.05 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <div className="text-green-600 dark:text-green-400">
                                    <TrendingUp className="w-3.5 h-3.5" />
                                  </div>
                                </motion.div>
                                <div className="truncate text-center leading-tight">
                                  Success Rate
                                </div>
                              </motion.div>
                              {/* Checklist Completion Rate Column Header */}
                              <motion.div
                                className="w-44 px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide flex flex-col items-center justify-center gap-1.5 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 hover:from-purple-50 hover:to-purple-100 dark:hover:from-purple-900/20 dark:hover:to-purple-800/20 transition-all duration-200 border-l border-gray-200 dark:border-gray-600"
                                transition={{ duration: 0.2 }}
                              >
                                <motion.div
                                  className="p-1 rounded-md bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600"
                                  whileHover={{ rotate: 5, scale: 1.05 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <div className="text-purple-600 dark:text-purple-400">
                                    <ClipboardCheck className="w-3.5 h-3.5" />
                                  </div>
                                </motion.div>
                                <div className="truncate text-center leading-tight">
                                  Checklist Rate
                                </div>
                              </motion.div>
                            </div>
                          </div>

                          {/* Scrollable Rows */}
                          <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {sortedCoaches.map((coach, index) => {
                              const activeCount = coachData[coach] || 0;
                              const totalCount = totalCoachData[coach] || 0;
                              const avgDays = avgDaysData[coach] || 0;
                              const wonCount = successWonData[coach] || 0;
                              const closedTotal = successTotalData[coach] || 0;
                              const successRate =
                                closedTotal > 0 ? (wonCount / closedTotal) * 100 : 0;
                              const checklistComplete = checklistCompleteData[coach] || 0;
                              const checklistTotal = checklistTotalData[coach] || 0;
                              const checklistRate =
                                checklistTotal > 0 ? (checklistComplete / checklistTotal) * 100 : 0;
                              const colorScheme = getRowColorScheme(index);

                              return (
                                <motion.div
                                  key={`${coach}-metrics`}
                                  className={`group flex border-b border-gray-200 dark:border-gray-700 hover:${colorScheme.light} hover:${colorScheme.dark} transition-all duration-300`}
                                  initial={{ opacity: 0, x: 20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: index * 0.05 + 0.2, duration: 0.4 }}
                                >
                                  {/* Active Onboardings Value */}
                                  <div className="w-44 px-4 py-3 flex items-center justify-center h-14 border-l border-gray-200 dark:border-gray-600">
                                    <motion.div
                                      className="flex items-center justify-center gap-2"
                                      whileHover={{ scale: 1.05 }}
                                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                                    >
                                      <div className="flex flex-col items-center">
                                        <span className="text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-400 dark:to-emerald-400 bg-clip-text text-transparent">
                                          {activeCount}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          clinics
                                        </span>
                                      </div>
                                    </motion.div>
                                  </div>
                                  {/* Total Onboardings Value */}
                                  <div className="w-44 px-4 py-3 flex items-center justify-center h-14 border-l border-gray-200 dark:border-gray-600">
                                    <motion.div
                                      className="flex items-center justify-center gap-2"
                                      whileHover={{ scale: 1.05 }}
                                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                                    >
                                      <div className="flex flex-col items-center">
                                        <span className="text-lg font-bold bg-gradient-to-r from-amber-600 to-orange-600 dark:from-amber-400 dark:to-orange-400 bg-clip-text text-transparent">
                                          {totalCount}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          total
                                        </span>
                                      </div>
                                    </motion.div>
                                  </div>
                                  {/* Avg Days Value */}
                                  <div className="w-44 px-4 py-3 flex items-center justify-center h-14 border-l border-gray-200 dark:border-gray-600">
                                    <motion.div
                                      className="flex items-center justify-center gap-2"
                                      whileHover={{ scale: 1.05 }}
                                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                                    >
                                      <div className="flex flex-col items-center">
                                        <span className="text-lg font-bold bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-400 dark:to-blue-400 bg-clip-text text-transparent">
                                          {avgDays > 0 ? avgDays.toFixed(1) : '-'}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          days
                                        </span>
                                      </div>
                                    </motion.div>
                                  </div>
                                  {/* Success Rate Value */}
                                  <div className="w-44 px-4 py-3 flex items-center justify-center h-14 border-l border-gray-200 dark:border-gray-600">
                                    <motion.div
                                      className="flex items-center justify-center gap-2"
                                      whileHover={{ scale: 1.05 }}
                                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                                    >
                                      <div className="flex flex-col items-center">
                                        <span className="text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-400 dark:to-emerald-400 bg-clip-text text-transparent">
                                          {closedTotal > 0 ? `${successRate.toFixed(1)}%` : '-'}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {closedTotal > 0
                                            ? `${wonCount}/${closedTotal}`
                                            : 'no data'}
                                        </span>
                                      </div>
                                    </motion.div>
                                  </div>
                                  {/* Checklist Completion Rate Value */}
                                  <div className="w-44 px-4 py-3 flex items-center justify-center h-14 border-l border-gray-200 dark:border-gray-600">
                                    <motion.div
                                      className="flex items-center justify-center gap-2"
                                      whileHover={{ scale: 1.05 }}
                                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                                    >
                                      <div className="flex flex-col items-center">
                                        <span className="text-lg font-bold bg-gradient-to-r from-purple-600 to-violet-600 dark:from-purple-400 dark:to-violet-400 bg-clip-text text-transparent">
                                          {checklistTotal > 0
                                            ? `${checklistRate.toFixed(1)}%`
                                            : '-'}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {checklistTotal > 0
                                            ? `${checklistComplete}/${checklistTotal}`
                                            : 'no data'}
                                        </span>
                                      </div>
                                    </motion.div>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })()}
          </Tabs.Content>
        </Tabs.Root>

        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
          <div className="absolute top-20 right-10 w-32 h-32 bg-gradient-to-br from-green-400/10 to-emerald-400/10 rounded-full blur-3xl"></div>
          <div className="absolute top-96 left-10 w-24 h-24 bg-gradient-to-br from-cyan-400/10 to-blue-400/10 rounded-full blur-2xl"></div>
          <div className="absolute bottom-20 right-20 w-28 h-28 bg-gradient-to-br from-orange-400/10 to-pink-400/10 rounded-full blur-3xl"></div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingDashboard;
